import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Input, Pill } from "../../components/common/Primitives";
import { Coins, TrendingUp, FileSearch, AlertTriangle, ChevronDown, Wand2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useFocusHighlight from "../../lib/useFocusHighlight";

const GROUP_LABELS = {
  revenue: "Revenue",
  profitability: "Profitability",
  cost_and_burn: "Cost & Burn",
  balance_sheet: "Balance Sheet",
  unit_economics: "Unit Economics",
  funding: "Funding & Cap Table",
};

const FIELD_LABELS = {
  monthly_revenue: "Monthly Revenue", annual_revenue: "Annual Revenue", arr: "ARR",
  revenue_growth_yoy: "Revenue Growth YoY %", revenue_growth_mom: "Revenue Growth MoM %",
  gross_profit: "Gross Profit", gross_margin_pct: "Gross Margin %",
  ebitda: "EBITDA", ebitda_margin_pct: "EBITDA Margin %",
  pat: "PAT / Net Profit", net_profit_margin_pct: "Net Profit Margin %",
  loss_amount: "Loss Amount", break_even_status: "Break-Even Status",
  monthly_burn: "Monthly Burn", fixed_costs: "Fixed Costs", variable_costs: "Variable Costs",
  employee_cost: "Employee Cost", marketing_cost: "Marketing Cost", tech_cost: "Tech Cost",
  runway_months: "Runway (months)",
  authorised_capital: "Authorised Capital", paid_up_capital: "Paid-Up Capital",
  net_worth: "Net Worth", debt: "Debt", loans_and_borrowings: "Loans & Borrowings",
  trade_receivables: "Trade Receivables", trade_payables: "Trade Payables",
  cash_and_bank: "Cash & Bank", current_assets: "Current Assets",
  current_liabilities: "Current Liabilities", contingent_liabilities: "Contingent Liab.",
  cac: "CAC", ltv: "LTV", aov: "AOV",
  gross_margin_per_unit: "Gross Margin/Unit", contribution_margin_pct: "Contribution Margin %",
  retention_rate_pct: "Retention %", churn_rate_pct: "Churn %", payback_period_months: "Payback (mo)",
  existing_investors: "Existing Investors", previous_rounds: "Previous Rounds",
  amount_raised_till_date: "Total Raised", current_valuation_expectation: "Valuation Expectation",
  esop_pool_pct: "ESOP Pool %", promoter_holding_pct: "Promoter %",
  external_investor_holding_pct: "External Investor %", debt_funding_availed: "Debt Funding",
  grants_subsidies: "Grants / Subsidies",
};

const SOURCE_META = {
  api_fetched: { label: "API", tone: "success" },
  document_parsed: { label: "PARSED", tone: "gold" },
  founder_declared: { label: "FOUNDER", tone: "warn" },
  admin_entered: { label: "ADMIN", tone: "success" },
  admin_verified: { label: "VERIFIED", tone: "success" },
  conflicting: { label: "CONFLICT", tone: "danger" },
  mca_derived: { label: "MCA", tone: "success" },
  unverified: { label: "UNVERIFIED", tone: "muted" },
};

const SEVERITY_TONE = { high: "danger", medium: "warn", low: "muted" };

const DOC_TYPES = [
  { value: "financial_statement", label: "Audited Financial Statement" },
  { value: "provisional_financials", label: "Provisional Financials" },
  { value: "mis", label: "MIS / Management Dashboard" },
  { value: "gst_return", label: "GST Return" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "pitch_deck", label: "Pitch Deck" },
];

export default function StartupFinancialProfile() {
  useFocusHighlight();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [openGroup, setOpenGroup] = useState("revenue");
  const [extractDoc, setExtractDoc] = useState({ doc_type: "mis", filename: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/startup/financial-profile");
      setData(data);
      setDrafts(data.financial_profile.fields || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const acceptSuggested = () => {
    const suggested = data?.suggested_from_funding_profile || {};
    setDrafts({ ...drafts, ...suggested });
    toast.success(`Auto-filled ${Object.keys(suggested).length} field(s) from your funding profile.`);
  };

  const saveGroup = async (group) => {
    const fields = data.field_groups[group];
    const patch = {};
    for (const f of fields) {
      if (drafts[f] !== undefined && drafts[f] !== data.financial_profile.fields?.[f]) {
        patch[f] = drafts[f];
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save in this section.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/startup/financial-profile", { updates: patch, source: "founder_declared" });
      toast.success(`Saved ${Object.keys(patch).length} field(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runExtract = async () => {
    if (!extractDoc.filename.trim()) {
      toast.error("Enter a filename to simulate extraction.");
      return;
    }
    setBusy(true);
    try {
      const { data: resp } = await api.post("/startup/financial-profile/extract", extractDoc);
      const conflicts = resp.conflicts?.length || 0;
      toast.success(`Extracted ${Object.keys(resp.extraction.extracted).length} fields. ${conflicts ? `${conflicts} conflict(s) — check flags.` : ""}`);
      setExtractDoc({ ...extractDoc, filename: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <DashboardShell title="Financial Profile"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  const fp = data.financial_profile;
  const scores = fp.scores || {};
  const flags = fp.red_flags || [];
  const suggested = data.suggested_from_funding_profile || {};
  const suggestedCount = Object.keys(suggested).length;
  const missingFromSuggested = Object.entries(suggested).filter(([k]) => drafts[k] == null).length;

  return (
    <DashboardShell title="Financial Profile" subtitle="STARTUP · DILIGENCE-READY DATA">
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          {/* Auto-pull banner */}
          {missingFromSuggested > 0 && (
            <Card testId="fp-autopull-banner" className="bg-gold/[0.06] border-gold/40">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading text-ink text-sm mb-1">💡 We noticed you've already entered financial numbers in your funding profile.</div>
                  <div className="text-xs text-ink-muted">Auto-fill {missingFromSuggested} of {suggestedCount} matching field(s) — you can edit each before saving.</div>
                </div>
                <Button variant="gold" onClick={acceptSuggested} data-testid="fp-accept-suggested-btn"><Wand2 size={12} className="inline mr-1" /> Auto-fill</Button>
              </div>
            </Card>
          )}

          {/* Extraction card */}
          <Card testId="fp-extract-card">
            <div className="overline mb-3 flex items-center gap-2"><FileSearch size={14} /> Extract from Uploaded Document</div>
            <div className="text-xs text-ink-muted mb-3">Simulate parsing an uploaded financial doc into structured fields. Real integration will read your actual files.</div>
            <div className="flex gap-2 items-end">
              <div>
                <div className="overline mb-1">Document Type</div>
                <select value={extractDoc.doc_type} onChange={(e) => setExtractDoc({ ...extractDoc, doc_type: e.target.value })} className="border border-navy/15 rounded-md px-3 py-1.5 text-sm" data-testid="fp-extract-doctype">
                  {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <Input label="Filename" value={extractDoc.filename} onChange={(e) => setExtractDoc({ ...extractDoc, filename: e.target.value })} placeholder="mis_q4_2024.xlsx" testId="fp-extract-filename" />
              </div>
              <Button variant="gold" onClick={runExtract} disabled={busy} data-testid="fp-extract-btn">{busy ? "Extracting…" : "Extract"}</Button>
            </div>
          </Card>

          {/* Field groups */}
          <div data-focus="financials" data-focus-debt="debt">
          {Object.entries(data.field_groups).map(([groupKey, fields]) => (
            <Card key={groupKey} testId={`fp-group-${groupKey}`}>
              <button
                onClick={() => setOpenGroup(openGroup === groupKey ? null : groupKey)}
                className="w-full flex items-center justify-between mb-3"
                data-testid={`fp-group-toggle-${groupKey}`}
              >
                <div className="overline flex items-center gap-2"><Coins size={14} /> {GROUP_LABELS[groupKey]}</div>
                <ChevronDown size={16} className={`transition-transform ${openGroup === groupKey ? "rotate-180" : ""}`} />
              </button>
              {openGroup === groupKey && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {fields.map((f) => {
                      const src = fp.field_sources?.[f];
                      const meta = SOURCE_META[src?.source] || SOURCE_META.unverified;
                      const suggestedVal = suggested[f];
                      return (
                        <div key={f} className="border border-navy/10 rounded-md p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{FIELD_LABELS[f] || f}</div>
                            {src && <Pill tone={meta.tone} data-testid={`fp-src-${f}`}>{meta.label} {src.confidence && src.source !== "conflicting" ? `${src.confidence}%` : ""}</Pill>}
                          </div>
                          <input
                            type="number"
                            value={drafts[f] ?? ""}
                            onChange={(e) => setDrafts({ ...drafts, [f]: e.target.value === "" ? undefined : Number(e.target.value) })}
                            className="w-full border-b border-navy/15 focus:border-gold text-sm py-1 outline-none bg-transparent"
                            data-testid={`fp-input-${f}`}
                            placeholder={suggestedVal != null ? `Suggested: ${suggestedVal.toLocaleString("en-IN")}` : "—"}
                          />
                          {src?.source === "conflicting" && src.extracted_value != null && (
                            <div className="mt-1 text-[10px] text-rose-700 font-mono">
                              CONFLICT · Doc says: {src.extracted_value.toLocaleString("en-IN")}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="gold" onClick={() => saveGroup(groupKey)} disabled={busy} data-testid={`fp-save-${groupKey}`}>Save {GROUP_LABELS[groupKey]}</Button>
                  </div>
                </>
              )}
            </Card>
          ))}
          </div>
        </div>

        {/* Right rail: Scores + Flags */}
        <div className="space-y-4">
          <Card testId="fp-scores-card">
            <div className="overline mb-3 flex items-center gap-2"><TrendingUp size={14} /> Readiness Scores</div>
            <div className="mb-3">
              <div className="flex items-baseline justify-between">
                <div className="text-4xl font-display text-ink" data-testid="fp-overall-score">{scores.overall_health_score ?? "—"}</div>
                <div>
                  <Pill tone={
                    scores.label === "Investor Ready" ? "success" :
                    scores.label === "Almost Ready" ? "gold" :
                    scores.label === "Needs Review" ? "warn" :
                    scores.label === "High Risk" ? "danger" : "muted"
                  } data-testid="fp-score-label">{scores.label || "Insufficient Data"}</Pill>
                </div>
              </div>
              <div className="overline mt-1">Overall Health Score</div>
            </div>
            <div className="space-y-2 text-xs">
              <ScoreBar label="Company Verification" value={scores.company_verification_score} />
              <ScoreBar label="Financial Readiness" value={scores.financial_readiness_score} />
              <ScoreBar label="Documentation" value={scores.documentation_score} />
              <ScoreBar label="Compliance" value={scores.compliance_score} />
              <ScoreBar label="Investor Readiness" value={scores.investor_readiness_score} highlight />
            </div>
          </Card>

          <Card testId="fp-flags-card">
            <div className="overline mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Red Flags ({flags.length})</div>
            {flags.length === 0 ? (
              <div className="text-sm text-ink-muted italic flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-600" /> No red flags — clean data.</div>
            ) : (
              <div className="space-y-2">
                {flags.map((f, i) => (
                  <div key={f.flag_id || i} className="border-l-2 pl-3 py-1" style={{ borderColor: f.severity === "high" ? "#EF4444" : f.severity === "medium" ? "#F59E0B" : "#9CA3AF" }} data-testid={`fp-flag-${f.code}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <Pill tone={SEVERITY_TONE[f.severity]}>{f.severity}</Pill>
                      <span className="text-[10px] font-mono text-ink-muted">{f.code}</span>
                    </div>
                    <div className="text-xs text-ink">{f.message}</div>
                    {f.suggestion && <div className="text-[10px] text-ink-muted italic mt-1">→ {f.suggestion}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}

function ScoreBar({ label, value, highlight }) {
  const v = value ?? 0;
  const color = v >= 80 ? "bg-emerald-500" : v >= 60 ? "bg-gold" : v >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex justify-between">
        <span className={highlight ? "font-heading text-ink" : "text-ink-muted"}>{label}</span>
        <span className="font-mono">{value ?? "—"}</span>
      </div>
      <div className="h-1.5 bg-navy/[0.06] rounded-full mt-1 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}
